<template>
  <ion-app>
    <OfflineBanner />
    <ion-router-outlet />
    <PusherStatus />
  </ion-app>
</template>

<script setup lang="ts">
import { IonApp, IonRouterOutlet } from "@ionic/vue";
import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { Keyboard } from "@capacitor/keyboard";
import { LocalNotifications } from "@capacitor/local-notifications";
import { onMounted, onBeforeUnmount, watch } from "vue";
import { useRouter } from "vue-router";
import { useNotificationStore } from "@/stores/notificationStore";
import { useThemeStore, PHONE_LAYOUT } from "@/stores/themeStore";
import { useAuth } from "@/services/AuthService";
import { SchultenMedia } from "@/services/DataService";
import { applyBrandColor } from "@/utils/brandTheme";
import PusherStatus from "@/components/PusherStatus.vue";
import OfflineBanner from "@/components/OfflineBanner.vue";
import HealthKitService from "@/services/HealthKitService";
import HealthConnectService from "@/services/HealthConnectService";
import { BackgroundRunner } from "@capacitor/background-runner";
import { SplashScreen } from "@capacitor/splash-screen";
import { canonicalWorkoutSessionRoute } from "@/utils/workoutAutosave";
import { DISTRIBUTION } from "@/config/distribution";

const router = useRouter();
const notificationStore = useNotificationStore();
const themeStore = useThemeStore();
const { isAuthenticated } = useAuth();

// The general Coachio distribution may follow a signed-in tenant's color.
// Locked white-label distributions keep their compiled palette everywhere,
// including after login.
watch(
  [() => SchultenMedia.User?.branding?.color, () => isAuthenticated.value],
  ([color, authenticated]) => applyBrandColor(
    DISTRIBUTION.allowTenantColor && authenticated && color
      ? color
      : DISTRIBUTION.primaryColor,
  ),
  { immediate: true },
);

const setupBackgroundSystem = async () => {
  console.log("setupBackgroundSystem");
  try {
    await BackgroundRunner.requestPermissions({
      apis: ["notifications"],
    });
    console.log("Background permissions granted");
  } catch (error) {
    console.warn("Background permissions:", error);
  }

  // This is not possible
  // addEventListener("fetchBackground", async (resolve, reject) => {
  //   console.log("Background fetch triggered", new Date().toLocaleTimeString());
  //   try {
  //     await HealthKitService.backgroundFetch();
  //     resolve();
  //   } catch (err) {
  //     console.error(err);
  //     reject(err);
  //   }
  // });


  App.addListener("resume", async () => {
    console.log("App resumed, fetching health data...");

    await HealthKitService.backgroundFetch();

    // Health Connect (Android) background sync
    try {
      await HealthConnectService.backgroundFetch();
    } catch (error) {
      console.error("HealthConnectService.backgroundFetch error:", error);
    }
  });
};

onMounted(async () => {
  if (Capacitor.isNativePlatform()) {
    await SplashScreen.hide();
  }

  // Listen for local notification taps (e.g. rest timer completion)
  if (Capacitor.isNativePlatform()) {
    LocalNotifications.addListener("localNotificationActionPerformed", (action) => {
      const route = action.notification.extra?.route;
      if (route) {
        // Rest alerts created by older builds can still contain the exercise card's
        // one-shot `?startAt=0`. The saved session index is newer and authoritative.
        router.push(canonicalWorkoutSessionRoute(route));
      }
    });
  }

  // Android resets the system-bar icon color (light/dark content) after
  // keyboard show/hide, app resume, and some route transitions, leaving the
  // status bar/nav bar icons unreadable on a white background. We re-apply
  // the current theme's style on those events. System-bar backgrounds are
  // supplied by the edge-to-edge page itself; this only updates icon appearance.
  if (Capacitor.isNativePlatform()) {
    App.addListener("resume", () => {
      themeStore.reapplySystemBars();
    });

    if (Capacitor.getPlatform() === "android") {
      Keyboard.addListener("keyboardWillShow", () => {
        document.body.classList.add("keyboard-open");
      });
      Keyboard.addListener("keyboardWillHide", () => {
        document.body.classList.remove("keyboard-open");
      });
      Keyboard.addListener("keyboardDidHide", () => {
        themeStore.reapplySystemBars();
      });
    }

    // Also picks the status bar for a signed-out screen with a brand photo. The first
    // route resolved before this listener existed, and a rotation can switch layouts.
    router.afterEach((to) => themeStore.followRoute(to));
    themeStore.followRoute(router.currentRoute.value);
    window.matchMedia(PHONE_LAYOUT).addEventListener("change", () => {
      themeStore.followRoute(router.currentRoute.value);
    });
  }

  if (isAuthenticated) {
    const isAppleHealthAvailable = await HealthKitService.isAvailable();
    console.log('isAppleHealthAvailable', isAppleHealthAvailable)
    if (isAppleHealthAvailable) {
      await HealthKitService.checkAuthorization(async function (success, provider) {
        console.log('HealthKitService.checkAuthorization', success, provider)
        if (success) {
          await setupBackgroundSystem();
        }
      });
    }

    // Health Connect (Android) startup check
    try {
      const isHealthConnectAvailable = await HealthConnectService.isAvailable();
      console.log('isHealthConnectAvailable', isHealthConnectAvailable);
      if (isHealthConnectAvailable) {
        await HealthConnectService.checkAuthorization(async function (success, provider) {
          console.log('HealthConnectService.checkAuthorization', success, provider);
          if (success) {
            await setupBackgroundSystem();
          }
        });
      }
    } catch (error) {
      console.error("HealthConnectService initialization error:", error);
    }

    // Initialize notifications when app starts
    await notificationStore.fetchNotifications();

    // Set up auto-refresh for notifications
    const refreshInterval = setInterval(async () => {
      await notificationStore.fetchNotifications();
    }, 30000); // 30 seconds

    // Clean up interval when component unmounts
    onBeforeUnmount(() => {
      clearInterval(refreshInterval);
    });
  }
});
</script>

<style>
@import url("https://fonts.googleapis.com/icon?family=Material+Icons");
@import url("https://fonts.googleapis.com/css2?family=Material+Icons+Round");

* {
  font-family: "Poppins", sans-serif;
}

body {
  font-family: "Poppins", sans-serif;
}

.font-poppins {
  font-family: "Poppins", sans-serif !important;
}
</style>
